import React, { useCallback, useEffect, useState } from "react";
import PropTypes from "prop-types";
import withRouter from "../Components/Common/withRouter";

import Header from "./Header";
import Sidebar from "./Sidebar";
import Footer from "./Footer";
import ErrorBoundary from "../Components/Common/ErrorBoundary";
import SuperAdminLayoutMenuData from "./SuperAdminLayoutMenuData";
import { LayoutShellProvider } from "./MenuDataContext";
import {
  TENANT_LAYOUT_TYPE,
  TENANT_SIDEBAR_SIZE,
} from "../helpers/layout_defaults";

import {
  changeLayout,
  changeSidebarTheme,
  changeLayoutMode,
  changeLayoutThemeColor,
  changeLayoutTheme,
  changeLayoutWidth,
  changeLayoutPosition,
  changeTopbarTheme,
  changeLeftsidebarSizeType,
  changeLeftsidebarViewType,
  changeSidebarImageType,
  changeSidebarVisibility,
} from "../slices/thunks";

import { useSelector, useDispatch } from "react-redux";
import { createSelector } from "reselect";

/** SA + tenant default: vertical + lg (ícone + rótulo i18n) — ADJ-F2-131. */
const SA_LAYOUT_TYPE = TENANT_LAYOUT_TYPE;
const SA_SIDEBAR_SIZE = TENANT_SIDEBAR_SIZE;

const AdminVerticalLayout = (props: any) => {
  const [headerClass, setHeaderClass] = useState("");
  const dispatch: any = useDispatch();

  const selectLayoutState = (state: any) => state.Layout;
  const selectLayoutProperties = createSelector(selectLayoutState, (layout) => ({
    layoutType: layout.layoutType,
    leftSidebarType: layout.leftSidebarType,
    layoutModeType: layout.layoutModeType,
    layoutThemeColorType: layout.layoutThemeColorType,
    layoutThemeType: layout.layoutThemeType,
    layoutWidthType: layout.layoutWidthType,
    layoutPositionType: layout.layoutPositionType,
    topbarThemeType: layout.topbarThemeType,
    leftsidbarSizeType: layout.leftsidbarSizeType,
    leftSidebarViewType: layout.leftSidebarViewType,
    leftSidebarImageType: layout.leftSidebarImageType,
    preloader: layout.preloader,
    sidebarVisibilitytype: layout.sidebarVisibilitytype,
  }));

  const {
    layoutType,
    leftSidebarType,
    layoutModeType,
    layoutThemeColorType,
    layoutThemeType,
    layoutWidthType,
    layoutPositionType,
    topbarThemeType,
    leftsidbarSizeType,
    leftSidebarViewType,
    leftSidebarImageType,
    sidebarVisibilitytype,
  } = useSelector(selectLayoutProperties);

  // Force SA vertical+lg; on unmount restore tenant default (vertical+lg), never Two Column (ADJ-F2-131).
  useEffect(() => {
    document.body.classList.remove("twocolumn-panel");
    document.documentElement.setAttribute("data-shell", "super-admin");
    dispatch(changeLayout(SA_LAYOUT_TYPE));
    dispatch(changeLeftsidebarSizeType(SA_SIDEBAR_SIZE));

    return () => {
      document.documentElement.removeAttribute("data-shell");
      document.body.classList.remove("twocolumn-panel");
      dispatch(changeLayout(TENANT_LAYOUT_TYPE));
      dispatch(changeLeftsidebarSizeType(TENANT_SIDEBAR_SIZE));
    };
  }, [dispatch]);

  // j/k cycles SA sidebar links (skips support tickets list, which already uses j/k).
  useEffect(() => {
    const pathname: string = props.router?.location?.pathname || "";

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key !== "j" && event.key !== "k") {
        return;
      }

      if (pathname.startsWith("/super-admin/support/tickets")) {
        return;
      }

      const target = event.target as HTMLElement | null;
      const tag = target?.tagName;

      if (
        tag === "INPUT" ||
        tag === "TEXTAREA" ||
        tag === "SELECT" ||
        target?.isContentEditable
      ) {
        return;
      }

      const links = Array.from(
        document.querySelectorAll<HTMLAnchorElement>("#navbar-nav a.nav-link")
      ).filter((anchor) => {
        const href = anchor.getAttribute("href") || "";
        return href.startsWith("/super-admin");
      });

      if (links.length === 0) {
        return;
      }

      const pathIndex = links.findIndex((anchor) => {
        try {
          return new URL(anchor.href, window.location.origin).pathname === pathname;
        } catch {
          return false;
        }
      });
      const activeIndex =
        pathIndex >= 0
          ? pathIndex
          : links.findIndex((anchor) => anchor.classList.contains("active"));

      const delta = event.key === "j" ? 1 : -1;
      const nextIndex =
        activeIndex < 0
          ? event.key === "j"
            ? 0
            : links.length - 1
          : (activeIndex + delta + links.length) % links.length;

      event.preventDefault();
      links[nextIndex]?.click();
    };

    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [props.router?.location?.pathname]);

  useEffect(() => {
    if (
      leftSidebarType ||
      layoutModeType ||
      layoutThemeType ||
      layoutThemeColorType ||
      layoutWidthType ||
      layoutPositionType ||
      topbarThemeType ||
      leftSidebarViewType ||
      leftSidebarImageType ||
      sidebarVisibilitytype
    ) {
      window.dispatchEvent(new Event("resize"));
      dispatch(changeLeftsidebarViewType(leftSidebarViewType));
      // Keep SA on vertical + lg (Theme Customizer removed from SA shell — GAP-F2-164).
      dispatch(changeLeftsidebarSizeType(SA_SIDEBAR_SIZE));
      dispatch(changeSidebarTheme(leftSidebarType));
      dispatch(changeLayoutThemeColor(layoutThemeColorType));
      dispatch(changeLayoutTheme(layoutThemeType));
      dispatch(changeLayoutMode(layoutModeType));
      dispatch(changeLayoutWidth(layoutWidthType));
      dispatch(changeLayoutPosition(layoutPositionType));
      dispatch(changeTopbarTheme(topbarThemeType));
      dispatch(changeLayout(SA_LAYOUT_TYPE));
      dispatch(changeSidebarImageType(leftSidebarImageType));
      dispatch(changeSidebarVisibility(sidebarVisibilitytype));
    }
  }, [
    layoutType,
    leftsidbarSizeType,
    leftSidebarType,
    layoutModeType,
    layoutThemeType,
    layoutThemeColorType,
    layoutWidthType,
    layoutPositionType,
    topbarThemeType,
    leftSidebarViewType,
    leftSidebarImageType,
    sidebarVisibilitytype,
    dispatch,
  ]);

  const scrollNavigation = useCallback(() => {
    const scrollup = document.documentElement.scrollTop;

    if (scrollup > 50) {
      setHeaderClass("topbar-shadow");
    } else {
      setHeaderClass("");
    }
  }, []);

  useEffect(() => {
    window.addEventListener("scroll", scrollNavigation, true);
    return () => window.removeEventListener("scroll", scrollNavigation, true);
  }, [scrollNavigation]);

  useEffect(() => {
    const humberIcon = document.querySelector(".hamburger-icon") as HTMLElement | null;
    if (!humberIcon) {
      return;
    }

    if (sidebarVisibilitytype === "show") {
      humberIcon.classList.remove("open");
    } else {
      humberIcon.classList.add("open");
    }
  }, [sidebarVisibilitytype]);

  return (
    <LayoutShellProvider
      navData={SuperAdminLayoutMenuData}
      homePath="/super-admin/dashboard"
    >
      <React.Fragment>
        <div id="layout-wrapper">
          <Header headerClass={headerClass} />
          <Sidebar layoutType={SA_LAYOUT_TYPE} />
          <div className="main-content">
            <ErrorBoundary resetKey={props.router?.location?.pathname}>
              {props.children}
            </ErrorBoundary>
            <Footer />
          </div>
        </div>
      </React.Fragment>
    </LayoutShellProvider>
  );
};

AdminVerticalLayout.propTypes = {
  children: PropTypes.object,
};

export default withRouter(AdminVerticalLayout);
